import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type TimelineChannel,
  messageTransportValues,
  useCustomerTimeline,
} from '@/features/conversations/api';
import { useState } from 'react';
import { PlatformBadge } from './platform-badge';

/**
 * Where a reply goes in the merged inbox: one conversation can span the
 * customer's WhatsApp, SMS, Messenger… threads. The reply defaults to the
 * channel they last wrote on; staff can pick another they can be reached on.
 * A phone thread is never a reply channel.
 *
 * `merged` is false for an ordinary single-thread conversation, where the
 * composer works exactly as before.
 */
export function useReplyTarget(conversationId: string) {
  const { channels, replyConversationId } = useCustomerTimeline(conversationId);
  const [picked, setPicked] = useState<string | null>(null);
  const replyable = channels.filter((c) => c.canReply);
  const merged = channels.length > 1;
  const target =
    (picked && replyable.some((c) => c.conversationId === picked)
      ? picked
      : null) ??
    replyConversationId ??
    replyable[0]?.conversationId ??
    null;
  return { merged, replyable, target, setTarget: setPicked };
}

export function ReplyChannelPicker({
  replyable,
  target,
  onChange,
}: {
  replyable: TimelineChannel[];
  target: string;
  onChange: (conversationId: string) => void;
}) {
  const current = replyable.find((c) => c.conversationId === target);
  if (!current) return null;
  if (replyable.length === 1) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        Reply via{' '}
        <PlatformBadge
          platform={current.platform}
          transport={current.transport}
          className="py-0.5 text-[11px]"
        />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      Reply via
      <Select value={target} onValueChange={onChange}>
        <SelectTrigger
          aria-label="Reply channel"
          className="h-7 w-auto gap-1 border-0 px-1.5 shadow-none"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {replyable.map((c) => (
            <SelectItem key={c.conversationId} value={c.conversationId}>
              <PlatformBadge platform={c.platform} transport={c.transport} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** A centered channel chip where the conversation moves to another channel. */
export function ChannelSwitch({
  platform,
  transport,
}: {
  platform: TimelineChannel['platform'];
  transport?: unknown;
}) {
  const reportedTransport = messageTransportValues.find(
    (value) => value === transport
  );
  return (
    <div className="flex justify-center">
      <PlatformBadge platform={platform} transport={reportedTransport} />
    </div>
  );
}
